import { useMemo } from "react";
import { GitCompare } from "lucide-react";

// Shared diff utilities modelled on Enquiry Costing's inline version diff.
// Palette: yellow = changed, green = added (only in target), red = removed (only in this snapshot).

export function cellsEqual(a, b) {
  const na = a === null || a === undefined ? "" : String(a).trim();
  const nb = b === null || b === undefined ? "" : String(b).trim();
  if (na === nb) return true;
  const fa = parseFloat(na); const fb = parseFloat(nb);
  if (!Number.isNaN(fa) && !Number.isNaN(fb) && fa === fb) return true;
  return false;
}

export function DiffTable({ title, cols, snapRows = [], targetRows = [], colLabels }) {
  const maxLen = Math.max(snapRows.length, targetRows.length);
  const labels = cols.map((c) => (colLabels && colLabels[c]) || c.replace(/_/g, " "));
  if (maxLen === 0) {
    return (
      <div>
        <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-1">{title} · 0</div>
        <div className="text-[12px] text-slate-400 italic">Empty in both.</div>
      </div>
    );
  }
  return (
    <div>
      <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-1">{title} · {snapRows.length} / {targetRows.length}</div>
      <div className="rounded border border-slate-200 overflow-hidden">
        <table className="w-full text-[11.5px]">
          <thead>
            <tr className="bg-slate-50">
              <th className="text-left px-2 py-1 font-semibold text-slate-500 w-8">#</th>
              {cols.map((c, i) => <th key={c} className="text-left px-2 py-1 font-semibold text-slate-600 capitalize">{labels[i]}</th>)}
              <th className="text-right px-2 py-1 font-semibold text-slate-500 w-16">Status</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: maxLen }).map((_, i) => {
              const s = snapRows[i];
              const t = targetRows[i];
              const removed = s !== undefined && t === undefined;
              const added = s === undefined && t !== undefined;
              const rowClass = removed ? "bg-red-50" : added ? "bg-emerald-50" : "border-t border-slate-100";
              const statusBadge = removed
                ? <span className="text-[10px] font-semibold text-red-700">REMOVED</span>
                : added
                ? <span className="text-[10px] font-semibold text-emerald-700">ADDED</span>
                : <span className="text-[10px] font-mono text-slate-400">—</span>;
              return (
                <tr key={i} className={`border-t border-slate-100 ${rowClass}`} data-testid={`bdiff-row-${title}-${i}`}>
                  <td className="px-2 py-1 text-slate-400 font-mono">{i + 1}</td>
                  {cols.map((c) => {
                    if (added) return <td key={c} className="px-2 py-1 text-emerald-800"><b>{String(t?.[c] ?? "")}</b></td>;
                    if (removed) return <td key={c} className="px-2 py-1 text-red-800 line-through">{String(s?.[c] ?? "")}</td>;
                    const changed = !cellsEqual(s?.[c], t?.[c]);
                    return (
                      <td key={c} className={`px-2 py-1 ${changed ? "bg-yellow-100 text-yellow-900 font-semibold" : "text-slate-700"}`} title={changed ? `Target: ${String(t?.[c] ?? "—")}` : undefined}>
                        {String(s?.[c] ?? "")}
                        {changed && <span className="ml-1 text-[10px] font-mono text-yellow-700">← {String(t?.[c] ?? "—")}</span>}
                      </td>
                    );
                  })}
                  <td className="px-2 py-1 text-right">{statusBadge}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function DiffScalarBlock({ title, snap, target, fields }) {
  return (
    <div>
      <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-1">{title}</div>
      <div className="rounded border border-slate-200 overflow-hidden">
        <table className="w-full text-[11.5px]">
          <tbody>
            {fields.map(([k, label, fmt]) => {
              const sv = snap?.[k];
              const tv = target?.[k];
              const changed = !cellsEqual(sv, tv);
              const format = fmt || ((v) => (v === null || v === undefined ? "—" : String(v)));
              return (
                <tr key={k} className={`border-t border-slate-100 ${changed ? "bg-yellow-50" : ""}`} data-testid={`bdiff-scalar-${title}-${k}`}>
                  <td className="px-2 py-1 text-slate-500 w-64">{label}</td>
                  <td className={`px-2 py-1 ${changed ? "text-yellow-900 font-semibold" : "text-slate-700"}`}>
                    {format(sv)}
                    {changed && <span className="ml-2 text-[10px] font-mono text-yellow-700">← {format(tv)}</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Generic inline version-diff panel used by Enquiry Costing and Operation Bulletin.
 * Props:
 *  snapshot: version being inspected (older row)
 *  target: what to compare against (live/current OR previous version)
 *  targetLabel: string label for the target
 *  diffAgainst: "current" | "previous"
 *  setDiffAgainst: setter
 *  hasPrev: whether a previous version exists
 *  tables: [{ title, cols, snapRows, targetRows, colLabels?, keys? }]  — `keys` optional for summary counting
 *  scalars: [{ title, snap, target, fields: [[key, label, fmt?]] }]
 */
export function VersionDiffPanel({ snapshot, target, targetLabel, diffAgainst, setDiffAgainst, hasPrev, tables = [], scalars = [] }) {
  const summary = useMemo(() => {
    let changed = 0, added = 0, removed = 0;
    if (!target) return { changed: 0, added: 0, removed: 0 };
    for (const t of tables) {
      const s = t.snapRows || [];
      const g = t.targetRows || [];
      const keys = t.keys || t.cols;
      const max = Math.max(s.length, g.length);
      for (let i = 0; i < max; i++) {
        const sa = s[i], sb = g[i];
        if (sa && !sb) removed++;
        else if (!sa && sb) added++;
        else if (sa && sb) {
          for (const k of keys) if (!cellsEqual(sa[k], sb[k])) changed++;
        }
      }
    }
    for (const sc of scalars) {
      for (const [k] of sc.fields || []) {
        if (!cellsEqual(sc.snap?.[k], sc.target?.[k])) changed++;
      }
    }
    return { changed, added, removed };
  }, [snapshot, target, tables, scalars]);

  return (
    <div className="p-4 bg-slate-50/60 border-t-2 border-brand/30" data-testid="version-diff-view">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="text-[13px] font-heading font-semibold flex items-center gap-2">
          <GitCompare className="h-4 w-4 text-brand" />
          v{snapshot?.version} → {targetLabel}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="inline-flex items-center gap-1 text-[11px]" data-testid="diff-summary">
            <span className="px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-900 font-mono font-semibold">{summary.changed} changed</span>
            <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-mono font-semibold">{summary.added} added</span>
            <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-800 font-mono font-semibold">{summary.removed} removed</span>
          </div>
          <div className="inline-flex items-center gap-1 text-[11px]">
            <span className="uppercase tracking-widest text-slate-500 mr-1">Compare to:</span>
            <button
              onClick={() => setDiffAgainst("current")}
              className={`px-2 py-0.5 rounded border text-[11.5px] font-semibold ${diffAgainst === "current" ? "bg-brand text-white border-brand" : "bg-white text-slate-700 border-slate-200 hover:border-brand/40"}`}
              data-testid="diff-target-current"
            >Current</button>
            <button
              onClick={() => setDiffAgainst("previous")}
              disabled={!hasPrev}
              className={`px-2 py-0.5 rounded border text-[11.5px] font-semibold ${diffAgainst === "previous" ? "bg-brand text-white border-brand" : "bg-white text-slate-700 border-slate-200 hover:border-brand/40 disabled:opacity-40 disabled:cursor-not-allowed"}`}
              data-testid="diff-target-previous"
            >Previous version</button>
          </div>
        </div>
      </div>

      <div className="text-[10.5px] font-mono uppercase tracking-widest text-slate-500 flex items-center gap-3 mb-3">
        <span className="inline-flex items-center gap-1"><span className="inline-block h-3 w-3 rounded bg-yellow-200 border border-yellow-300"></span> changed</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block h-3 w-3 rounded bg-emerald-200 border border-emerald-300"></span> added</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block h-3 w-3 rounded bg-red-200 border border-red-300"></span> removed</span>
        <span className="text-slate-400">· value shown is v{snapshot?.version} · <b>←</b> points to target's value</span>
      </div>

      {!target ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-[12.5px] text-amber-900">
          No previous version exists to compare against. Switch to <b>Current</b>.
        </div>
      ) : (
        <div className="space-y-4">
          {tables.map((t) => (
            <DiffTable key={t.title} title={t.title} cols={t.cols} colLabels={t.colLabels} snapRows={t.snapRows} targetRows={t.targetRows} />
          ))}
          {scalars.map((s) => (
            <DiffScalarBlock key={s.title} title={s.title} snap={s.snap} target={s.target} fields={s.fields} />
          ))}
        </div>
      )}
    </div>
  );
}
